import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import posthog, { CaptureResult } from 'posthog-js';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';
import { EnvironmentService } from '../environment.service';

@Injectable({ providedIn: 'root' })
export class PosthogService {
  private router: Router = inject(Router);
  private environmentService: EnvironmentService = inject(EnvironmentService);

  private readonly platformId = inject(PLATFORM_ID);
  private initialized = false;

  /**
   * The posthog instance. Use this directly to call posthog methods.
   * Returns the actual posthog instance on browser, or a no-op proxy on server.
   */
  get posthog(): typeof posthog {
    if (isPlatformBrowser(this.platformId) && this.initialized) {
      return posthog;
    }
    // Return a no-op proxy for SSR safety
    return new Proxy({} as typeof posthog, {
      get: () => () => undefined,
    });
  }

  private parseHashUrl(parseUrl: string) {
    const url = new URL(parseUrl);

    const [beforeQuery, queryString = ''] = url.href.split('?');

    const hashIndex = beforeQuery.indexOf('#');
    const rawPath = beforeQuery.substring(hashIndex + 1);
    const pathname = rawPath === '/' ? '/' : rawPath.replace(/^\//, '');

    const params = new URLSearchParams(queryString);
    return {
      url: beforeQuery,
      pathname,
      householdId: params.get('householdId'),
      facilitatedEnrollmentId: params.get('facilitatedEnrollmentId'),
    };
  }

  init(): void {
    if (isPlatformBrowser(this.platformId) && !this.initialized) {
      const postHogConfig = this.environmentService.getPostHogConfig();
      if (postHogConfig && postHogConfig.projectToken?.length > 1 && postHogConfig.host?.length > 1) {
        posthog.init(postHogConfig.projectToken, {
          api_host: postHogConfig.host,
          capture_exceptions: false,
          capture_pageview: false,
          before_send: (event: CaptureResult | null): CaptureResult | null => {
            if (event !== null) {
              if (event?.properties['$current_url']) {
                const {url, pathname, householdId, facilitatedEnrollmentId} = this.parseHashUrl(event.properties['$current_url'])
                event.properties['$pathname'] = pathname;
                event.properties['$current_url'] = url;
                event.properties['$household_id'] = householdId;
                event.properties['$facilitated_enrollment_id'] = facilitatedEnrollmentId;
              }
              if (event.properties['$session_entry_url']) {
                const {url, pathname} = this.parseHashUrl(event.properties['$session_entry_url']);
                event.properties['$session_entry_pathname'] = pathname;
                event.properties['$session_entry_url'] = url;
              }
            }

            return event;
          },
          session_recording: {
            maskAllInputs: true,
            maskTextSelector: '*',
          },
        });

        /* 
          Angular's hash-based routing conflicts with PostHog's page tracking method, so we have to 
          manually fire pageview events to ensure every page navigation is captured

          That's why on the init we set capture_pageview to false
        */
        this.router.events
          .pipe(filter((event) => event instanceof NavigationStart))
          .subscribe((e: NavigationStart) => {
            this.posthog.capture('$pageview');
        });
      }
      this.initialized = true;
    }
  }
}
